/**
 * 日志上报队列管理器
 * 支持分级上报策略：立即上报(高优先级)和延迟批量上报(低优先级)
 */

export enum ReportPriority {
  IMMEDIATE = 'immediate', // 立即上报
  DEFERRED = 'deferred',   // 延迟上报
}

export interface MetricData {
  metric_name: string;
  tags: Record<string, any>;
  value: number;
  message: string;
  params?: any;
}

export class MetricReporter {
  private queue: MetricData[] = [];
  private timer: number | null = null;
  private readonly BATCH_SIZE: number = 10; // 批量上报阈值
  private readonly BATCH_TIMEOUT: number = 5000; // 5秒超时
  private metricFunction: ((data: MetricData | MetricData[]) => void) | null = null;
  private isUnloading: boolean = false;

  constructor() {
    this.setupBeforeUnload();
  }

  /**
   * 设置实际的上报函数
   */
  public setMetricFunction(fn: (data: MetricData | MetricData[]) => void) {
    this.metricFunction = fn;
  }

  /**
   * 上报日志 - 根据优先级选择策略
   */
  public report(data: MetricData, priority: ReportPriority = ReportPriority.DEFERRED) {
    if (!this.metricFunction) {
      console.warn('Metric function not set, queuing data');
      this.queue.push(data);
      return;
    }

    if (priority === ReportPriority.IMMEDIATE) {
      this.reportImmediate(data);
    } else {
      this.reportDeferred(data);
    }
  }

  /**
   * 立即上报(高优先级)
   * 用于：错误日志、关键失败、超时等
   */
  private reportImmediate(data: MetricData) {
    try {
      this.doReport(data);
    } catch (error) {
      console.error('Immediate report failed:', error);
      // 降级到队列
      this.queue.push(data);
    }
  }

  /**
   * 延迟上报(低优先级)
   * 用于：成功日志、性能指标、统计数据等
   */
  private reportDeferred(data: MetricData) {
    this.queue.push(data);

    // 达到批量大小，立即上报
    if (this.queue.length >= this.BATCH_SIZE) {
      this.flush();
      return;
    }

    // 已经有定时器在运行，不重复设置
    if (this.timer !== null) {
      return;
    }

    // 优先使用 requestIdleCallback，降级到 setTimeout
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(
        () => this.flush(),
        { timeout: this.BATCH_TIMEOUT }
      );
    } else {
      this.timer = window.setTimeout(() => this.flush(), this.BATCH_TIMEOUT);
    }
  }

  /**
   * 批量上报队列中的数据
   */
  private flush() {
    if (this.queue.length === 0) {
      this.timer = null;
      return;
    }

    const batch = [...this.queue];
    this.queue = [];
    this.timer = null;

    try {
      // 批量上报
      if (batch.length === 1) {
        this.doReport(batch[0]);
      } else {
        this.doReport(batch);
      }
    } catch (error) {
      console.error('Batch report failed:', error);
      // 失败的数据不重新入队，避免无限循环
    }
  }

  /**
   * 执行实际的上报操作
   */
  private doReport(data: MetricData | MetricData[]) {
    if (!this.metricFunction) {
      console.warn('Metric function not available');
      return;
    }

    // 如果是批量数据，逐个上报或者按照业务需求批量上报
    if (Array.isArray(data)) {
      // 批量上报可以减少网络请求次数
      // data.forEach(item => this.metricFunction?.(item));
      this.metricFunction(data);
    } else {
      this.metricFunction(data);
    }
  }

  /**
   * 页面卸载前强制上报所有队列数据
   */
  private beforeUnload = () => {
    if (this.isUnloading || this.queue.length === 0) {
      return;
    }

    this.isUnloading = true;

    try {
      // 使用 sendBeacon 保证数据发送
      const batch = [...this.queue];
      this.queue = [];

      // 使用 sendBeacon API 发送数据（如果可用）
      // 注意：这里仍然使用 metric 函数上报，因为 sendBeacon 需要特定的 endpoint
      // 在实际生产环境中，可能需要根据业务需求调整
      // batch.forEach(item => this.metricFunction?.(item));
      this.metricFunction?.(batch);
    } catch (error) {
      console.error('BeforeUnload report failed:', error);
    }
  };

  /**
   * 设置页面卸载监听
   */
  private setupBeforeUnload() {
    if (typeof window === 'undefined') {
      return;
    }

    // 监听 visibilitychange 事件（更可靠）
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.beforeUnload();
        }
      });
    }

    // 监听 pagehide 事件（移动端更可靠）
    window.addEventListener('pagehide', this.beforeUnload);

    // 监听 beforeunload 事件（兜底）
    window.addEventListener('beforeunload', this.beforeUnload);
  }

  /**
   * 手动触发上报队列中的所有数据
   */
  public forceFlush() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.flush();
  }

  /**
   * 获取当前队列长度
   */
  public getQueueLength(): number {
    return this.queue.length;
  }

  /**
   * 清空队列（慎用）
   */
  public clearQueue() {
    this.queue = [];
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

// 创建单例实例
const metricReporter = new MetricReporter();

export default metricReporter;
